import { useApolloClient } from "@apollo/client";
import { AutocompleteMultiple, useDebouncedValue } from "@msys/ui";
import { Chip } from "@mui/material";
import { identity } from "lodash-es";
import React from "react";
import {
  OrganisationMembershipsFilters,
  OrganisationMembershipsSorting,
  OrganisationMembershipStatus,
} from "../../../clients/graphqlTypes.js";
import {
  alphabeticSortByLabel,
  memberToMemberOption,
} from "../users/helpers.js";
import { UserAvatarFragment } from "../users/UserAvatar.generated.js";
import { UserAvatar } from "../users/UserAvatar.js";
import {
  useOrganisationMemberSelect_MemberOptionsQuery,
  useOrganisationMemberSelect_SelectedMembersQuery,
} from "./OrganisationMemberSelect.generated.js";
import { DEFAULT_SORTING } from "./OrganisationMemberSelect.js";

export interface OrganisationMembersSelectProps
  extends Omit<
    React.ComponentProps<
      typeof AutocompleteMultiple<
        {
          label: string;
          value: string;
          membershipStatus: OrganisationMembershipStatus;
          userAvatar: UserAvatarFragment;
        },
        false
      >
    >,
    | "onChange"
    | "value"
    | "options"
    | "renderOption"
    | "renderInput"
    | "getOptionLabel"
  > {
  value: string[];
  onChange: (value: string[]) => void;
  sorting?: Array<OrganisationMembershipsSorting>;
  filters?: OrganisationMembershipsFilters;
}

export const OrganisationMembersSelect = ({
  value,
  onChange,
  inputLabel,
  placeholder,
  required,
  disabled,
  readOnly,
  error,
  sorting = DEFAULT_SORTING,
  filters,
  filterSelectedOptions = true,
  ...props
}: OrganisationMembersSelectProps) => {
  const [inputValue, setInputValue] = React.useState("");
  const inputValueDebounced = useDebouncedValue(inputValue, 300);

  const client = useApolloClient();
  const memberOptionsQuery = useOrganisationMemberSelect_MemberOptionsQuery({
    client,
    fetchPolicy: "network-only",
    variables: {
      sorting,
      filters,
      search: inputValueDebounced,
    },
  });
  const memberOptions = React.useMemo(
    () =>
      (
        (
          memberOptionsQuery?.data ?? memberOptionsQuery?.previousData
        )?.organisationMemberships.edges.map(edge => edge.node) ?? []
      )
        .map(memberToMemberOption)
        .sort(alphabeticSortByLabel),
    [memberOptionsQuery?.data, memberOptionsQuery?.previousData]
  );

  const selectedMembersQuery = useOrganisationMemberSelect_SelectedMembersQuery(
    {
      client,
      variables: {
        sorting: DEFAULT_SORTING,
        selectedMembers: value,
        limit: value.length,
      },
      skip: value.length === 0,
    }
  );
  const selectedMembers = React.useMemo(
    () =>
      value.length === 0
        ? []
        : (
            selectedMembersQuery.data ?? selectedMembersQuery.previousData
          )?.selectedMembers.edges.map(edge => edge.node),
    [selectedMembersQuery.data, selectedMembersQuery.previousData, value.length]
  );
  const selectedMemberOptions = React.useMemo(() => {
    return value.length === 0
      ? []
      : (selectedMembers?.map(memberToMemberOption) ?? []);
  }, [selectedMembers, value.length]);

  return (
    <AutocompleteMultiple
      loading={memberOptionsQuery.loading}
      inputLabel={inputLabel}
      placeholder={placeholder}
      options={memberOptions}
      value={selectedMemberOptions}
      getOptionLabel={option => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      renderOption={(
        {
          // @ts-ignore
          key,
          ...props
        },
        option
      ) => (
        <li key={key} {...props}>
          <UserAvatar
            size="xs"
            userAvatar={option.userAvatar}
            style={{ marginRight: "8px" }}
          />
          {option.label}
        </li>
      )}
      required={required}
      disabled={disabled}
      readOnly={readOnly}
      renderValue={(value, getTagProps) =>
        value.map((value, index) => {
          const { key, ...props } = getTagProps({ index });
          return (
            <Chip
              avatar={<UserAvatar size="xs" userAvatar={value.userAvatar} />}
              variant="outlined"
              size="small"
              label={value.label}
              {...props}
            />
          );
        })
      }
      onInputChange={(event, newInputValue, reason) => {
        if (reason === "reset") {
          setInputValue("");
          return;
        }
        setInputValue(newInputValue);
      }}
      onChange={value => {
        onChange(value.map(v => v.value));
      }}
      error={error}
      filterSelectedOptions={filterSelectedOptions}
      filterOptions={identity}
      {...props}
    />
  );
};
